import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CropField,
  CropVariety,
  FieldsDialogModel,
  GrasslandUsage,
  SiteState,
  SoilAnalysis,
} from 'src/app/shared/models';
import { SoilAnalysisDesignations } from 'src/app/shared/models/soil-analysis.model';
import {
  getCropVarietiesByCropId,
  getCropYear,
  getSoilAnalysisByCropFieldId,
} from 'src/app/store/selectors';

import {
  ManureSpaceSettings,
  ManureSpaceSettingToggles,
  NitrogenCalculationSettings,
} from '../models/manure-space-settings.models';
import { getCropFieldSettings, getCropFieldSettingsTogglesByCropFieldId } from '../store/selectors';

@Component({
  selector: 'app-field-details',
  templateUrl: './field-details.component.html',
  standalone: false,
})
export class FieldDetailsComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);

  @Input() cropField: CropField;
  @Input() formGroup: FormGroup;
  @Input() data: FieldsDialogModel;
  @Output() formDataLoaded = new EventEmitter<void>();
  grasslandUsageKeys: string[];
  settingsTogglesVisibility?: ManureSpaceSettingToggles = null;
  displayCrop = '';
  displayCropVariety = '';
  soilAnalysis: SoilAnalysis;
  cropVarieties: CropVariety[] = [];
  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  cropYear$ = this.store.select(getCropYear);
  characteristicPAL = '-';
  characteristicPPAE = '-';
  characteristicPw = '-';

  ngOnInit() {
    this.grasslandUsageKeys = Object.keys(GrasslandUsage);
    this.displayCrop = this.getDisplayCrop(this.cropField.cropId);

    this.getFieldData();
    this.setInitialFormGroupData();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getFieldData() {
    this.store
      .pipe(select(getSoilAnalysisByCropFieldId, this.cropField.id), takeUntil(this.destroyed$))
      .subscribe((result) => {
        this.soilAnalysis = result?.soilAnalysis;
        this.characteristicPAL = this.getCharacteristicValue('P-AL');
        this.characteristicPPAE = this.getCharacteristicValue('P-PAE');
        this.characteristicPw = this.getCharacteristicValue('Pw');
      });
    this.store
      .pipe(select(getCropVarietiesByCropId, this.cropField.cropId), takeUntil(this.destroyed$))
      .subscribe((cropVarieties) => {
        if (cropVarieties.length && !this.displayCropVariety) {
          this.cropVarieties = cropVarieties;
          this.displayCropVariety = this.getDisplayCropVariety(this.cropField.cropVarietyId);
        }
      });
  }

  getNitrogenCalculationSettingsFormGroup(formGroup: FormGroup) {
    return formGroup.get('nitrogenCalculationSettings') as FormGroup;
  }

  getCharacteristicValue(designation: string) {
    if (!this.soilAnalysis) {
      return '-';
    }
    const characteristic = this.soilAnalysis.sampledCharacteristics.find(
      (char) => char.designation === designation,
    );
    if (!characteristic) {
      return '-';
    }
    return this.formatCharacteristicValue(characteristic?.value ?? 0, designation);
  }

  getDisplayCrop(cropId: number) {
    const displayName = cropId
      ? this.data.crops.find((crop) => crop.id === cropId)?.agrifirmCrop?.name
      : null;
    return displayName ?? '';
  }

  getDisplayCropVariety(cropVarietyId: number) {
    return !cropVarietyId
      ? ''
      : this.cropVarieties.find((variety) => variety.id === cropVarietyId)?.name;
  }

  formatCharacteristicValue = (value: number, designation: string): string =>
    value?.toFixed(this.getDecimalCount(designation)) ?? '-';

  private getDecimalCount = (designation: string): number => {
    switch (designation) {
      case SoilAnalysisDesignations.cnRatio:
      case SoilAnalysisDesignations.nlv:
      case SoilAnalysisDesignations.nTot:
      case SoilAnalysisDesignations.nMinVrdVv:
      case SoilAnalysisDesignations.pAl:
      case SoilAnalysisDesignations.pw:
      case SoilAnalysisDesignations.kGetal:
        return 0;
      default:
        return 1;
    }
  };

  private setInitialFormGroupData() {
    const cropFieldSettings$ = this.store.select(getCropFieldSettings);
    const cropFieldSettingsTogglesByCropFieldId$ = this.store.pipe(
      select(getCropFieldSettingsTogglesByCropFieldId, this.cropField.id),
    );

    combineLatest([cropFieldSettings$, cropFieldSettingsTogglesByCropFieldId$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([cropFieldSettings, toggleVisibility]) => {
        const settings = cropFieldSettings.find(
          (setting) => setting.cropFieldId === this.cropField.id,
        );
        const nitrogenCalculationSettings = settings?.nitrogenCalculationSettings;
        this.settingsTogglesVisibility = toggleVisibility;

        if (settings && toggleVisibility) {
          this.setInitialValues(
            this.formGroup,
            settings,
            nitrogenCalculationSettings,
            toggleVisibility,
          );
          this.setInitialValidators(this.formGroup, toggleVisibility);
          this.formDataLoaded.emit();
        }
      });
  }

  private setInitialValues(
    fg: FormGroup,
    s: ManureSpaceSettings,
    n: NitrogenCalculationSettings,
    toggleVisibility: ManureSpaceSettingToggles,
  ) {
    fg.get('id').setValue(s.id);
    fg.get('shouldBeUsedInLegalStandard').setValue(s.shouldBeUsedInLegalStandard);
    fg.get('shouldUseTransitionalArrangement').setValue(s.shouldUseTransitionalArrangement);
    fg.get('shouldUseLowInPhosphateArrangement').setValue(s.shouldUseLowInPhosphateArrangement);

    const nitrogenFg = fg.get('nitrogenCalculationSettings');
    if (nitrogenFg) {
      if (toggleVisibility.shouldShowPreviousCropCornToggle) {
        nitrogenFg.get('isPreviousCropCorn').setValue(n?.isPreviousCropCorn ?? false);
      }
      nitrogenFg.get('isRecoveryFertilisation').setValue(n?.isRecoveryFertilisation ?? false);
      if (toggleVisibility.shouldShowPreviousCropGrainToggle) {
        nitrogenFg.get('isPreviousCropNotGrain').setValue(n?.isPreviousCropNotGrain ?? false);
      }
      if (toggleVisibility.shouldShowShouldSplitNormToggle) {
        nitrogenFg.get('shouldSplitNorm').setValue(n?.shouldSplitNorm ?? false);
      }
      if (toggleVisibility.shouldShowNitrogenDifferentiationToggle) {
        nitrogenFg.get('isNitrogenDifferentiation').setValue(n?.isNitrogenDifferentiation ?? false);
      }
      if (toggleVisibility.shouldShowAfterTornGrassToggle) {
        nitrogenFg.get('isAfterTornGrass').setValue(n?.isAfterTornGrass ?? false);
      }
      if (toggleVisibility.shouldShowGrasslandUsage) {
        nitrogenFg.get('grasslandUsage').setValue(n?.grasslandUsage ?? false);
      }
    }
  }

  private setInitialValidators(fg: FormGroup, toggleVisibility: ManureSpaceSettingToggles) {
    fg.get('shouldBeUsedInLegalStandard').setValidators([Validators.required]);
    fg.get('shouldUseTransitionalArrangement').setValidators([Validators.required]);
    fg.get('shouldUseLowInPhosphateArrangement').setValidators([Validators.required]);

    const nitrogenFg = fg.get('nitrogenCalculationSettings');
    if (toggleVisibility.shouldShowPreviousCropCornToggle) {
      nitrogenFg.get('isPreviousCropCorn').setValidators([Validators.required]);
    }
    nitrogenFg.get('isRecoveryFertilisation').setValidators([Validators.required]);
    if (toggleVisibility.shouldShowPreviousCropGrainToggle) {
      nitrogenFg.get('isPreviousCropNotGrain').setValidators([Validators.required]);
    }
    if (toggleVisibility.shouldShowShouldSplitNormToggle) {
      nitrogenFg.get('shouldSplitNorm').setValidators([Validators.required]);
    }
    if (toggleVisibility.shouldShowNitrogenDifferentiationToggle) {
      nitrogenFg.get('isNitrogenDifferentiation').setValidators([Validators.required]);
    }
    if (toggleVisibility.shouldShowAfterTornGrassToggle) {
      nitrogenFg.get('isAfterTornGrass').setValidators([Validators.required]);
    }
    if (toggleVisibility.shouldShowGrasslandUsage) {
      nitrogenFg.get('grasslandUsage').setValidators([Validators.required]);
    }
  }
}
